import { computed, onMounted, onUnmounted, ref, watch, type ComputedRef, type Ref } from 'vue';

/**
 * How far past the top the scroller must be before the target may hide at all.
 *
 * Context: below this the target always shows. Without it a short flick at the very top hides the
 * thing the user is looking at, and on a bounce-scrolling browser the rubber band alone would do
 * it.
 */
const REVEAL_ABOVE = 64;

/**
 * How much movement in one direction counts as intent.
 *
 * Context: a scroller emits a stream of one- and two-pixel deltas, and reacting to each makes the
 * target flicker as a thumb wobbles. Accumulating until the total passes this is what turns noise
 * into a gesture.
 */
const INTENT_THRESHOLD = 8;

/** What one scroller looked like at the last decision. */
interface ScrollerState {
  offset: number;
  /** `scrollHeight - clientHeight` — the furthest it can scroll. */
  range: number;
}

/**
 * Hides something while the user scrolls **down** and brings it back when they scroll **up**.
 *
 * Built for the mobile navigation bar, which is why it is a composable rather than a few lines in
 * a layout: two layouts mount that bar and the behaviour has to be the same in both.
 *
 * 🔴 **One listener, in the capture phase, and that is the whole trick.** Which element actually
 * scrolls differs per route — `#layout-container` for most, the view's own region for the five in
 * `DefaultLayout`'s `SELF_SCROLLING_ROUTES` — so a listener bound to a particular container would
 * have to know the routing. Scroll events do not bubble, but they *are* dispatched through the
 * capture phase, so a single capturing listener on `window` sees scrolling in any container
 * without knowing which one it is.
 *
 * The caller binds `targetRef` and `targetStyle` to the element that should hide. It slides out on
 * its own **margin** rather than a transform, deliberately: a transform would make the element a
 * containing block for fixed-position descendants, and the mobile nav's expanded menu is a
 * `fixed inset-0` overlay that would then be clipped to a 60px bar. Margin also reclaims the
 * space, so the content moves up into it rather than the bar merely sliding over the top.
 */
export interface HideOnScrollOptions {
  /**
   * A value that changes when the scrolling view is replaced — a route path, typically. The target
   * is revealed and every baseline forgotten whenever it changes.
   */
  resetOn?: () => unknown;
}

export const useHideOnScroll = (
  options?: HideOnScrollOptions,
): {
  isHidden: Ref<boolean>;
  targetRef: Ref<HTMLElement | null>;
  targetStyle: ComputedRef<{ marginTop: string }>;
} => {
  const isHidden = ref(false);
  const targetRef = ref<HTMLElement | null>(null);
  const targetHeight = ref(0);

  /** What each scroller looked like at the last decision — a page can have more than one. */
  let lastStates = new WeakMap<EventTarget, ScrollerState>();

  /** Movement accumulated in the current direction, reset whenever the direction changes. */
  let travelled = 0;

  /**
   * A scroller's offset and how far it *can* scroll. Both are needed: the offset is the gesture,
   * and the range is how we tell a gesture from our own animation.
   */
  const stateOf = (target: EventTarget | null): ScrollerState | null => {
    const element =
      target instanceof HTMLElement
        ? target
        : // The document scrolling, which is what a `scroll` event on `document` means.
          target === document
          ? document.documentElement
          : null;

    if (!element) return null;

    return {
      offset: element.scrollTop,
      range: Math.max(0, element.scrollHeight - element.clientHeight),
    };
  };

  const onScroll = (event: Event): void => {
    const target = event.target;
    if (!target) return;

    const state = stateOf(target);
    if (!state) return;

    const previous = lastStates.get(target);
    lastStates.set(target, state);

    /**
     * 🔴 **The scroller's own range moved, so this is our animation, not the reader.**
     *
     * Hiding the target reclaims its height, which makes a bounded scroller that much taller and
     * drops its maximum offset by the same amount. A reader sitting at the bottom is then clamped
     * upwards by the browser — arriving as a `scroll` event with a large negative delta, reading as
     * "scrolled up", showing the target again and shrinking the scroller back. Measured at the
     * bottom of a bounded scroller in Chromium: **107 hide/show flips in about 1.5 seconds with no
     * further input.**
     *
     * Keyed on the range rather than on a timer, deliberately. A settling *window* also breaks the
     * loop, but it does so by ignoring every scroll for its duration — including the reader
     * genuinely flicking back up, which is the commonest gesture there is right after the bar
     * hides. This ignores exactly the events the animation caused and nothing else, because a
     * reflow is precisely what changes the range while a scroll gesture never does.
     */
    if (previous && previous.range !== state.range) {
      travelled = 0;
      return;
    }

    const delta = state.offset - (previous?.offset ?? 0);

    if (state.offset <= REVEAL_ABOVE) {
      // Near the top the bar always shows, whatever the last gesture was — otherwise scrolling
      // back to the very top can leave it hidden with nothing left to scroll up and recover it.
      travelled = 0;
      isHidden.value = false;
      return;
    }

    // A direction change starts the count again, so the threshold measures intent in one
    // direction rather than net movement over a whole flick.
    if ((delta > 0 && travelled < 0) || (delta < 0 && travelled > 0)) travelled = 0;

    travelled += delta;

    if (travelled > INTENT_THRESHOLD) isHidden.value = true;
    if (travelled < -INTENT_THRESHOLD) isHidden.value = false;
  };

  /**
   * Measured rather than hardcoded. A fixed offset is what this whole area of the layout was
   * getting wrong — `h-[calc(100dvh-60px)]` assumed a 60px bar and silently stopped being true —
   * so the distance to travel comes from the element itself.
   */
  let observer: ResizeObserver | undefined;

  const measure = (): void => {
    targetHeight.value = targetRef.value?.offsetHeight ?? 0;
  };

  const targetStyle = computed(() => ({
    marginTop: isHidden.value ? `-${targetHeight.value}px` : '0px',
  }));

  /**
   * Brings the target back and forgets every scroller's baseline.
   *
   * 🔴 **Without this the target can be hidden with no way to get it back.** Navigation can replace
   * the inner scrolling element while the layout around it stays mounted, and a short destination
   * page emits no scroll event at all — so a nav hidden on the way down the previous page stays
   * hidden, on a page with nothing to scroll. Reproduced in Chromium and WebKit.
   *
   * The caller says when the view has changed through `resetOn`, rather than this reaching for the
   * router itself: the signal is the consumer's to define, and a composable in `composables/`
   * should not stop working outside a routed context.
   */
  const reset = (): void => {
    travelled = 0;
    lastStates = new WeakMap<EventTarget, ScrollerState>();
    isHidden.value = false;
  };

  if (options?.resetOn) watch(options.resetOn, reset);

  onMounted(() => {
    window.addEventListener('scroll', onScroll, { capture: true, passive: true });

    measure();

    // Guarded because jsdom implements `ResizeObserver` only where a test stubs it, and a layout
    // test should not have to.
    if (typeof ResizeObserver === 'undefined' || !targetRef.value) return;

    observer = new ResizeObserver(measure);
    observer.observe(targetRef.value);
  });

  onUnmounted(() => {
    window.removeEventListener('scroll', onScroll, { capture: true });
    observer?.disconnect();
  });

  return { isHidden, targetRef, targetStyle };
};
